import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = { class: "overflow-y-auto max-w-1/2" }
const _hoisted_2 = { class: "text-sm font-semibold text-gray-900" }
const _hoisted_3 = { class: "mt-2 grid grid-cols-7 text-xs leading-6 text-gray-500" }
const _hoisted_4 = { class: "isolate mt-2 grid grid-cols-7 gap-px rounded-lg bg-gray-200 text-sm shadow ring-1 ring-gray-200" }
const _hoisted_5 = { key: 0 }
const _hoisted_6 = {
  key: 0,
  class: "topLeftTriangle"
}
const _hoisted_7 = {
  key: 1,
  class: "bottomLeftTriangle"
}
const _hoisted_8 = { class: "grid grid-cols-2 justify-stretch gap-1 mt-6 text-sm w-auto" }
const _hoisted_9 = {
  key: 0,
  class: "flex items-center gap-1"
}
const _hoisted_10 = { class: "capitalize" }
const _hoisted_11 = { class: "justify-self-end" }
const _hoisted_12 = {
  key: 0,
  class: "flex items-center gap-1"
}
const _hoisted_13 = { class: "capitalize" }
const _hoisted_14 = {
  key: 0,
  class: "text-sm mt-2 flex gap-2"
}

import {
  useIsWorkingDay,
  useNonWorkingDaysByDaySet,
  useProjectDurationSettings,
} from "@oculai/planner";
import {
  eachDayOfInterval,
  endOfDay,
  endOfMonth,
  endOfWeek,
  format,
  startOfDay,
  startOfMonth,
  startOfWeek,
  eachMonthOfInterval,
} from "date-fns";
import { computed, PropType } from "vue";
import { useOutagesByRange } from "@/composables/outages";
import { useStreams } from "@/composables/stream";
import { workingDaysColorCodes } from "@/constants/workingDays";
import { apiClient } from "@/repositories/clients";
import processDurationService from "@/services/processDurationService";
import { ShortenedProcess } from "@/types/Process";


export default /*#__PURE__*/_defineComponent({
  __name: 'WorkingDaysCalendar',
  props: {
  processes: {
    type: Array as PropType<ShortenedProcess[]>,
    required: true,
  },
},
  setup(__props) {

const props = __props;

const { streams } = useStreams();
const { projectDurationSettings } = useProjectDurationSettings(apiClient);
const nonWorkingDaysByDaySet = useNonWorkingDaysByDaySet(apiClient);
const isWorkingDay = useIsWorkingDay(apiClient);

const firstProcessDate = computed(() =>
  processDurationService.getFirstProcessDate(props.processes),
);
const lastProcessDate = computed(() => processDurationService.getLastProcessDate(props.processes));
const outagesInterval = computed(() => {
  return {
    start: firstProcessDate.value,
    end: lastProcessDate.value,
  };
});
const { outagesByRange } = useOutagesByRange(outagesInterval);
const processDateInterval = computed(() => processDurationService.getDateInterval(props.processes));

const visibleMonthsStartDate = computed(() =>
  eachMonthOfInterval({
    start: firstProcessDate.value,
    end: lastProcessDate.value,
  }),
);

const outages = computed(() => {
  if (!nonWorkingDaysByDaySet.value) {
    return outagesByRange.value;
  }

  return processDurationService.useCorrectOutages(
    outagesByRange.value,
    streams.value,
    nonWorkingDaysByDaySet.value,
    isWorkingDay.value,
  );
});

const processDetailMap = computed(() => {
  if (!projectDurationSettings.value) {
    return {};
  }

  return processDurationService.getProcessDetailMap(
    props.processes,
    projectDurationSettings.value,
    outages.value,
  );
});

const processDetailSummary = computed(() => {
  return processDurationService.getProcessDetailSummary(processDetailMap.value);
});

const totalDuration = computed(() => {
  return processDurationService.getTotalDuration(processDetailSummary.value);
});

const allCalendarDates = computed(() => {
  const result: string[][] = [];

  visibleMonthsStartDate.value.forEach((item: Date) => {
    const startOfCurrentMonth = startOfMonth(item);
    const endOfCurrentMonth = endOfMonth(item);
    const start = startOfWeek(startOfCurrentMonth, { weekStartsOn: 1 });
    const end = endOfWeek(endOfCurrentMonth, { weekStartsOn: 1 });

    const dates = eachDayOfInterval({ start: startOfDay(start), end: endOfDay(end) }).map((date) =>
      format(date, "yyyy-MM-dd"),
    );
    result.push(dates);
  });

  return result;
});

const isWithinWorkingInterval = (day: string) => {
  return processDateInterval.value.some((item: Date) => format(item, "yyyy-MM-dd") === day);
};

const isCurrentMonth = (currMonth: Date, date: string) => {
  return currMonth.getMonth() === new Date(date).getMonth();
};

const getBackgroundColor = (day: string, firstDayOfMonth: Date) => {
  if (!isCurrentMonth(firstDayOfMonth, day)) {
    return "";
  }

  const item = processDetailMap.value[day];

  if (item && item.working_day_count > 0) {
    if (item.status === "holiday") {
      return workingDaysColorCodes.holiday;
    }
    if (item.status === "critical_disturbance") {
      return workingDaysColorCodes.critical_disturbance;
    }
    if (item.non_critical_disturbances.length === 0) {
      if (item.status === "active") {
        return workingDaysColorCodes.active;
      } else if (item.status === "outage") {
        return workingDaysColorCodes.outage;
      } else {
        return workingDaysColorCodes.inactive;
      }
    }
    if (item.working_day_count > 0 && item.status) {
      return workingDaysColorCodes[item.status] || "";
    }
  }
  return "";
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", {
      class: _normalizeClass([
        'relative gap-x-8 gap-y-4 mt-4',
        visibleMonthsStartDate.value.length >= 4
          ? 'grid sm:grid-cols-2 md:grid-cols-4'
          : 'flex flex-wrap',
      ])
    }, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(visibleMonthsStartDate.value, (firstDayOfMonth, monthIdx) => {
        return (_openBlock(), _createElementBlock("section", { key: monthIdx }, [
          _createElementVNode("h2", _hoisted_2, _toDisplayString(_ctx.$t(`calendar.months.${_unref(format)(firstDayOfMonth, "M")}.long`)) + " " + _toDisplayString(_unref(format)(firstDayOfMonth, "yyyy")), 1),
          _createElementVNode("div", _hoisted_3, [
            (_openBlock(), _createElementBlock(_Fragment, null, _renderList(7, (index) => {
              return _createElementVNode("div", { key: index }, _toDisplayString(_ctx.$t(`calendar.week_days.${index}.short`).charAt(0)), 1)
            }), 64))
          ]),
          _createElementVNode("div", _hoisted_4, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(allCalendarDates.value[monthIdx], (day, dayIdx) => {
              return (_openBlock(), _createElementBlock("div", {
                key: dayIdx,
                class: _normalizeClass(["p-1.5 focus:z-10 relative text-center", [
              dayIdx === 0 && 'rounded-tl-lg',
              dayIdx === 6 && 'rounded-tr-lg',
              dayIdx === allCalendarDates.value[monthIdx].length - 7 && 'rounded-bl-lg',
              dayIdx === allCalendarDates.value[monthIdx].length - 1 && 'rounded-br-lg',
              {
                'bg-gray-400 text-white':
                  isWithinWorkingInterval(day) && isCurrentMonth(firstDayOfMonth, day),
                'bg-white-500 hover:bg-gray-100':
                  (!isWithinWorkingInterval(day) && !processDetailMap.value[day]) ||
                  !isCurrentMonth(firstDayOfMonth, day),
                [`diagonal-${processDetailMap.value[day]?.status}`]:
                  processDetailMap.value[day]?.working_day_count === 0.5 &&
                  isCurrentMonth(firstDayOfMonth, day),
                startDate:
                  day === _unref(format)(new Date(firstProcessDate.value), 'yyyy-MM-dd') &&
                  isCurrentMonth(firstDayOfMonth, day),
                endDate:
                  day === _unref(format)(new Date(lastProcessDate.value), 'yyyy-MM-dd') &&
                  isCurrentMonth(firstDayOfMonth, day),
              },
            ]]),
                style: _normalizeStyle({
              backgroundColor: getBackgroundColor(day, firstDayOfMonth),
              color:
                (!isWithinWorkingInterval(day) && !processDetailMap.value[day]) ||
                !isCurrentMonth(firstDayOfMonth, day)
                  ? 'gray'
                  : 'white',
            })
              }, [
                (isCurrentMonth(firstDayOfMonth, day))
                  ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                      (processDetailMap.value[day]?.non_critical_disturbances.length > 0)
                        ? (_openBlock(), _createElementBlock("span", _hoisted_6))
                        : _createCommentVNode("", true),
                      (
                  (processDetailMap.value[day]?.holidays.length > 0 ||
                    processDetailMap.value[day]?.critical_disturbances.length > 0) &&
                  processDetailMap.value[day].processes.length > 0
                )
                        ? (_openBlock(), _createElementBlock("span", _hoisted_7))
                        : _createCommentVNode("", true),
                      _createElementVNode("time", null, _toDisplayString(_unref(format)(new Date(day), "d")), 1)
                    ]))
                  : _createCommentVNode("", true)
              ], 6))
            }), 128))
          ])
        ]))
      }), 128))
    ], 2),
    _createElementVNode("div", _hoisted_8, [
      _createElementVNode("div", null, [
        (_openBlock(), _createElementBlock(_Fragment, null, _renderList(['active', 'inactive', 'outage'], (item) => {
          return _createElementVNode("div", { key: item }, [
            (processDetailSummary.value[item as keyof typeof processDetailSummary] !== 0)
              ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                  _createElementVNode("span", {
                    style: _normalizeStyle({ backgroundColor: _unref(workingDaysColorCodes)[item] }),
                    class: "w-2.5 h-2.5 rounded-full"
                  }, null, 4),
                  _createElementVNode("small", _hoisted_10, [
                    _createTextVNode(_toDisplayString(_ctx.$t(`working_day.${item}`)) + ": ", 1),
                    _createElementVNode("span", null, _toDisplayString(processDetailSummary.value[item as keyof typeof processDetailSummary]), 1),
                    _createTextVNode(" " + _toDisplayString(processDetailSummary.value[item as keyof typeof processDetailSummary] > 1
                  ? _ctx.$t("working_day.working_day", 2)
                  : _ctx.$t("working_day.working_day", 1)), 1)
                  ])
                ]))
              : _createCommentVNode("", true)
          ])
        }), 64))
      ]),
      _createElementVNode("div", _hoisted_11, [
        (_openBlock(), _createElementBlock(_Fragment, null, _renderList(['holiday', 'critical_disturbance'], (item) => {
          return _createElementVNode("div", { key: item }, [
            (processDetailSummary.value[item as keyof typeof processDetailSummary] !== 0)
              ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
                  _createElementVNode("div", {
                    style: _normalizeStyle({ backgroundColor: _unref(workingDaysColorCodes)[item] }),
                    class: "w-2.5 h-2.5 rounded-full"
                  }, null, 4),
                  _createElementVNode("small", _hoisted_13, [
                    _createTextVNode(_toDisplayString(_ctx.$t(`working_day.${item}`)) + ": ", 1),
                    _createElementVNode("span", null, _toDisplayString(processDetailSummary.value[item as keyof typeof processDetailSummary]), 1),
                    _createTextVNode(" " + _toDisplayString(processDetailSummary.value[item as keyof typeof processDetailSummary] > 1
                  ? _ctx.$t("working_day.working_day", 2)
                  : _ctx.$t("working_day.working_day", 1)), 1)
                  ])
                ]))
              : _createCommentVNode("", true)
          ])
        }), 64))
      ])
    ]),
    (totalDuration.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
          _createElementVNode("small", null, _toDisplayString(_ctx.$t("working_day.working_day", totalDuration.value)) + ": " + _toDisplayString(totalDuration.value), 1)
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})